import {
  Component,
  ElementRef,
  ViewChild,
  inject,
  OnInit
} from '@angular/core';
import {
  AbstractControl, FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { CdkDragEnd } from '@angular/cdk/drag-drop';
import { Fleet, FleetImage, FleetImageMarker, FleetImageMarkerModel, FleetModel } from '../../_models/fleet.models';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FleetsService } from '../../_services/fleets.service';
import { Constants } from '../../helpers/constants';
import { LoginHelper } from '../../helpers';

@Component({
  selector: 'fleet',
  standalone: false,
  templateUrl: './fleet.component.html',
  styleUrls: ['./fleet.component.scss'],
  providers: [DialogService],
})

export class FleetComponent implements OnInit {
  api: string;
  token: string;
  fleets: Fleet[] = [];
  selectedFleet?: Fleet | null;
  selectedImage?: FleetImage | null;
  addMarkerMode = false;
  isBusy = false;
  errorMessage = '';
  showMarkers = false;
  displayFleetCreateOrEditDialog = false;
  fleetName?: string | null = null;
  fleetDescription?: string | null = null;
  fleetOwner?: string | null = null;
  lastAssessed?: string | null = null;
  nextAssessment?: string | null = null;
  supportContact?: string | null = null;
  maintenance?: string | null = null;

  @ViewChild('imageSurface') imageSurface?: ElementRef<HTMLDivElement>;

  constructor(
    public fleetsService: FleetsService,
  ) {
    this.api = window.location.origin || Constants.DefaultApiHost;
    this.token = LoginHelper.getFileToken();
  }

  ngOnInit(): void {
    this.loadFleets();
  }

  loadFleets(): void {
    this.fleetsService.getAll().subscribe({
      next: fleets => {
        this.selectedFleet = fleets[0] ?? null;
        this.selectedImage = this.selectedFleet?.fleetImages?.length > 0 ? this.selectedFleet.fleetImages[0] : null;
        this.fleets = fleets;
      },
      error: () => {
        this.errorMessage = 'Failed to load fleets.';
      }
    });
  }

  onCreateFleet(): void {
    this.selectedFleet = null;
    this.selectedImage = null;
    this.fleetName = this.fleetDescription = this.fleetOwner = this.lastAssessed = this.nextAssessment = this.supportContact = this.maintenance = null;
    this.closeOpenFleetCreateOrEditDialog();
  };

  onEditFleet(): void {
    this.fleetName = this.selectedFleet.name;
    this.fleetDescription = this.selectedFleet.description;
    this.fleetOwner = this.selectedFleet.owner;
    this.lastAssessed = this.selectedFleet.lastAssessed;
    this.nextAssessment = this.selectedFleet.nextAssessment;
    this.supportContact = this.selectedFleet.supportContact;
    this.maintenance = this.selectedFleet.maintenance;
    this.closeOpenFleetCreateOrEditDialog();
  };

  onDeleteFleet(): void {
    // TODO: confirm popup

    this.deleteFleet();
  };

  closeOpenFleetCreateOrEditDialog(): void {
    this.displayFleetCreateOrEditDialog = !this.displayFleetCreateOrEditDialog;
    this.isBusy = false;
  };

  saveFleet(): void {
    const name = this.fleetName.trim();
    if (!name) {
      return;
    }

    this.isBusy = true;

    let fleet: FleetModel = this.selectedFleet ??
    {
      fleetId: 0,
      name: '',
      description: '',
      fleetImages: [],
      creationDate: null,
    };

    fleet.name = this.fleetName;
    fleet.description = this.fleetDescription;
    fleet.owner = this.fleetOwner;
    fleet.lastAssessed = this.lastAssessed;
    fleet.nextAssessment = this.nextAssessment;
    fleet.supportContact = this.supportContact;
    fleet.maintenance = this.maintenance;

    if (this.selectedFleet) {
      this.fleetsService.update(fleet).subscribe({
        next: fleet => {
        }
      });
    }
    else {
      this.fleetsService.create(fleet).subscribe({
        next: fleet => {
          this.fleets = [...this.fleets, fleet].sort((a, b) => a.name.localeCompare(b.name));
          this.selectedFleet = fleet;
          this.selectedImage = null;
        }
      });
    }

    this.closeOpenFleetCreateOrEditDialog();
  }

  deleteFleet(): void {
    if (!this.selectedFleet) {
      return;
    }

    this.isBusy = true;

    this.fleetsService.delete(this.selectedFleet.fleetId).subscribe({
      next: () => {
        this.fleets = this.fleets.filter(fleet => fleet.fleetId !== this.selectedFleet.fleetId);
        this.selectedFleet = null;
        this.selectedImage = null;
      }
    });
  }

  selectFleet(fleet: Fleet): void {
    this.selectedFleet = fleet;
    this.selectedImage = this.selectedFleet?.fleetImages?.length > 0 ? this.selectedFleet.fleetImages[0] : null;
  }

  getImageUrl(fileName: string): string {
    return `${this.api}/api/attachments/file?name=${encodeURIComponent(fileName)}&asImage=true&path=Account/Fleets&token=${this.token}`;
  }

  selectImage(image: FleetImage): void {
    this.selectedImage = image;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file || !this.selectedFleet) {
      return;
    }

    this.isBusy = true;

    this.fleetsService
      .uploadImage(this.selectedFleet.fleetId, file)
      .subscribe((fleetImage) => {
        if (!this.selectedFleet!.fleetImages) {
          this.selectedFleet!.fleetImages = [];
        }
        fleetImage.fleetId = this.selectedFleet.fleetId;
        this.selectedFleet!.fleetImages = [...this.selectedFleet!.fleetImages, fleetImage].sort((a, b) => a.sortOrder - b.sortOrder);
        this.selectedImage = fleetImage;
        this.isBusy = false;
        });
  }

  deleteSelectedImage(): void {
    if (!this.selectedFleet || !this.selectedImage) {
      return;
    }
    this.isBusy = true;
    this.selectedFleet!.fleetImages = this.selectedFleet!.fleetImages.filter(x => x.fleetImageId !== this.selectedImage!.fleetImageId);
    this.fleetsService.updateDeep(this.selectedFleet).subscribe({
      next: fleet => {
        this.selectedImage = this.selectedFleet?.fleetImages?.length > 0 ? this.selectedFleet.fleetImages[0] : null;
        this.isBusy = false;
      },
      error: () => {
        this.errorMessage = 'Failed to delete image.';
        this.isBusy = false;
      }
    });
  }

  dropThumbnail(event: CdkDragDrop<FleetImage[]>): void {
    if (!this.selectedFleet) {
      return;
    }

    moveItemInArray(this.selectedFleet.fleetImages, event.previousIndex, event.currentIndex);
    this.selectedFleet.fleetImages.forEach((x, index) => (x.sortOrder = index));
    this.isBusy = true;
    this.fleetsService.updateDeep(this.selectedFleet).subscribe({
      next: fleet => {
        this.selectedImage = this.selectedFleet?.fleetImages?.length > 0 ? this.selectedFleet.fleetImages[0] : null;
        this.isBusy = false;
      },
      error: () => {
        this.errorMessage = 'Failed to save image order.';
        this.isBusy = false;
      }
    });
  }

  toggleMarkerMode(): void {
    this.addMarkerMode = !this.addMarkerMode;
  }

  addMarkerOnImage(event: MouseEvent): void {
    if (!this.addMarkerMode || !this.selectedImage || !this.imageSurface) {
      return;
    }

    const rect = this.imageSurface.nativeElement.getBoundingClientRect();
    const x = this.clamp((event.clientX - rect.left) / rect.width);
    const y = this.clamp((event.clientY - rect.top) / rect.height);

    const marker: FleetImageMarkerModel = {
      fleetImageMarkerId: crypto.randomUUID().toString(),
      pointX: 0,
      pointY: 0,
      name: `Marker ${this.selectedImage.fleetImageMarkers.length + 1}`,
      sortOrder: this.selectedImage.fleetImageMarkers.length,
      fleetImageId: '',
      deviceId: 0,
      description: '',
      creationDate: ''
    };
    this.selectedImage.fleetImageMarkers = [...this.selectedImage.fleetImageMarkers, marker];
  }

  markerDropped(event: CdkDragEnd, marker: FleetImageMarker): void {
    if (!this.selectedImage || !this.imageSurface) {
      return;
    }

    const surfaceRect = this.imageSurface.nativeElement.getBoundingClientRect();
    const markerRect = event.source.element.nativeElement.getBoundingClientRect();

    const centerX = markerRect.left - surfaceRect.left;
    const tipY = markerRect.top - surfaceRect.top;

    marker.pointX = this.clamp(centerX / surfaceRect.width);
    marker.pointY = this.clamp(tipY / surfaceRect.height);

    event.source.reset();
  }

  saveMarkers(): void {
    if (!this.selectedFleet || !this.selectedImage) {
      return;
    }

    const markers = this.selectedImage.fleetImageMarkers
      .map((m, index) => ({ ...m, sortOrder: index }));

    //this.api.saveMarkers(this.selectedFleet.id, this.selectedImage.id, markers).subscribe({
    //  error: () => {
    //    this.errorMessage = 'Failed to save markers.';
    //  }
    //});
  }

  removeMarker(markerId: string): void {
    if (!this.selectedImage) {
      return;
    }

    this.selectedImage.fleetImageMarkers = this.selectedImage.fleetImageMarkers
      .filter(x => x.fleetImageMarkerId !== markerId)
      .map((x, index) => ({ ...x, sortOrder: index }));
  }

  leftPercent(marker: FleetImageMarker): string {
    return `${marker.pointX * 100}%`;
  }

  topPercent(marker: FleetImageMarker): string {
    return `${marker.pointY * 100}%`;
  }

  private clamp(value: number): number {
    console.log(value);
    return Math.max(0, Math.min(1, value));
  }
}
